import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * Admin customer and KYC endpoints (spec §30, §31).
 *
 * The types mirror the API's mappers rather than the Prisma models — the server
 * deliberately returns a narrower shape than it stores, and duplicating the
 * model here would invite reading fields that never arrive.
 */

export type CustomerStatus =
  | 'REGISTERED'
  | 'PROFILE_INCOMPLETE'
  | 'VERIFICATION_PENDING'
  | 'UNDER_REVIEW'
  | 'VERIFIED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'BLOCKED';

export type DocumentStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface CustomerListItem {
  id: string;
  customerCode: string;
  companyName: string;
  businessType: string;
  gstNumber: string | null;
  status: CustomerStatus;
  isVerified: boolean;
  canOrder: boolean;
  securityDepositStatus: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  assignedSalesAgent: { id: string; name: string } | null;
  documentCount: number;
  verifiedAt: string | null;
  createdAt: string;
}

export interface VerificationDocument {
  id: string;
  type: string;
  fileName: string;
  fileSizeBytes: number;
  mimeType: string;
  documentNumber: string | null;
  status: DocumentStatus;
  rejectionReason: string | null;
  reviewedAt: string | null;
  uploadedAt: string;
  downloadPath: string;
  reviewedBy?: string | null;
}

export interface VerificationHistoryEntry {
  id: string;
  action: string;
  previousStatus: CustomerStatus | null;
  newStatus: CustomerStatus;
  notes: string | null;
  performedBy: string | null;
  createdAt: string;
}

export interface SecurityDeposit {
  requiredAmount: number;
  paidAmount: number;
  heldAmount: number;
  refundedAmount: number;
  balanceAmount: number;
  outstandingAmount: number;
  status: string;
  currency: string;
}

export interface CustomerDetail {
  id: string;
  customerCode: string;
  companyName: string;
  businessType: string;
  gstNumber: string | null;
  panNumber: string | null;
  businessRegistrationNumber: string | null;
  website: string | null;
  yearsInBusiness: number | null;
  expectedMonthlyVolume: number | null;
  status: CustomerStatus;
  isVerified: boolean;
  canOrder: boolean;
  securityDepositStatus: string;
  verifiedAt: string | null;
  rejectionReason: string | null;
  internalNotes: string | null;
  creditLimit: number | null;
  verifiedBy: string | null;
  lastLoginAt: string | null;
  contact: {
    id: string;
    email: string;
    mobile: string | null;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    mobileVerified: boolean;
  } | null;
  assignedSalesAgent: { id: string; name: string; email: string } | null;
  addresses: Array<{
    id: string;
    label: string;
    contactName: string;
    contactPhone: string;
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    country: string;
    pincode: string;
    gstin: string | null;
    isDefault: boolean;
  }>;
  documents: VerificationDocument[];
  verificationHistory: VerificationHistoryEntry[];
  securityDeposit: SecurityDeposit | null;
  orderCount: number;
  createdAt: string;
}

export interface QueueSummary {
  byStatus: Partial<Record<CustomerStatus, number>>;
  awaitingReview: number;
  pendingDocuments: number;
}

export interface CustomerListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: CustomerStatus[];
  awaitingReview?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const keys = {
  all: ['admin', 'customers'] as const,
  list: (params: CustomerListParams) => [...keys.all, 'list', params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
  summary: () => [...keys.all, 'summary'] as const,
};

export function useCustomerQueue(params: CustomerListParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<CustomerListItem>>('/admin/customers', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          // The API accepts a comma-separated list for multi-status filtering.
          status: params.status?.length ? params.status.join(',') : undefined,
          awaitingReview: params.awaitingReview || undefined,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useCustomerDetail(customerId: string | null) {
  return useQuery({
    queryKey: keys.detail(customerId ?? ''),
    queryFn: () => apiClient.get<CustomerDetail>(`/admin/customers/${customerId}`),
    enabled: customerId !== null,
  });
}

export function useQueueSummary() {
  return useQuery({
    queryKey: keys.summary(),
    queryFn: () => apiClient.get<QueueSummary>('/admin/customers/summary'),
  });
}

type DecisionBody = Record<string, unknown> | undefined;

/**
 * Any workflow action against one customer.
 *
 * They all invalidate the same three things — the detail, the queue and the
 * counters — so they share one hook rather than repeating that in six places.
 */
export function useCustomerAction(customerId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      action,
      body,
      method = 'POST',
    }: {
      action: string;
      body?: DecisionBody;
      method?: 'POST' | 'PATCH';
    }) => {
      const path = `/admin/customers/${customerId}/${action}`;

      return method === 'PATCH'
        ? apiClient.patch<CustomerDetail>(path, body ?? {})
        : apiClient.post<CustomerDetail>(path, body ?? {});
    },
    onSuccess: (detail) => {
      queryClient.setQueryData(keys.detail(detail.id), detail);
      void queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useReviewDocument(customerId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      decision,
      rejectionReason,
    }: {
      documentId: string;
      decision: 'APPROVE' | 'REJECT';
      rejectionReason?: string;
    }) =>
      apiClient.patch<VerificationDocument>(`/admin/customers/documents/${documentId}`, {
        decision,
        ...(rejectionReason ? { rejectionReason } : {}),
      }),
    onSuccess: () => {
      if (customerId) {
        void queryClient.invalidateQueries({ queryKey: keys.detail(customerId) });
      }
      void queryClient.invalidateQueries({ queryKey: keys.summary() });
    },
  });
}

/**
 * Open a KYC document.
 *
 * These routes need the bearer header, so the bytes are fetched and handed to
 * the browser as an object URL rather than linked to directly.
 */
export async function openDocument(document_: VerificationDocument): Promise<void> {
  const blob = await apiClient.blob(document_.downloadPath);
  const url = URL.createObjectURL(blob);

  window.open(url, '_blank', 'noopener');

  // Give the new tab time to claim the URL before releasing it.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
